import { useSyncExternalStore, type ReactNode } from 'react'
import { Navigate, Outlet } from 'react-router'
import { useAuth } from '@/features/auth/use-auth'
import { useCurrentTeam } from '@/features/team/current-team'
import type { Feature, Role } from '@/lib/types'
import { FullPageLoader } from '@/components/full-page-loader'
import { registeringStore } from '@/lib/firebase'

/** Signed in, email verified, team loaded — the v1 PrivateRoute checks, in order. */
export function RequireAuth() {
  const { user, loading } = useAuth()
  const { loading: teamLoading } = useCurrentTeam()

  if (loading) return <FullPageLoader />
  if (!user) return <Navigate to="/login" replace />
  if (teamLoading) return <FullPageLoader />
  if (user.emailVerified === false) return <Navigate to="/verify-email" replace />
  return <Outlet />
}

export function Gate({
  roles,
  feature,
  paidOnly,
  children,
}: {
  roles?: Role[]
  feature?: Feature
  paidOnly?: boolean
  children: ReactNode
}) {
  const { current, hasFeature } = useCurrentTeam()
  if (current && roles && !roles.includes(current.role)) return <Navigate to="/" replace />
  if (!hasFeature(feature)) return <Navigate to="/plan/upgrade" replace />
  if (paidOnly && current?.plan?.Name === 'Free') return <Navigate to="/plan/upgrade" replace />
  return children
}

/** Auth pages bounce a signed-in user to the app. */
export function GuestOnly() {
  const { user, loading } = useAuth()
  const registering = useSyncExternalStore(registeringStore.subscribe, registeringStore.get)
  if (loading) return <FullPageLoader />
  if (user && !registering) return <Navigate to="/" replace />
  return <Outlet />
}
